import React, { useContext } from 'react';
import { Popover, StatusIndicator } from '@amzn/awsui-components-react/polaris';
import DateUtils from 'src/utils/dateUtils';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ColumnDef, PullStatusResponse, SegmentPeriodInfo } from 'src/models/segment-cdt/SegmentCdtTypes';
import {
    ColumnPullRow,
    columnPullRows,
    isTerminal,
    pullPresentation,
    runFailureText,
} from 'src/components/segment-cdt/detail/segmentPullStatus';

// `DateUtils.formatTimestamp` rather than `FormattingService`'s: it renders in America/Los_Angeles with the
// zone spelled out (`2026/09/09 11:12 PM PDT`), which is what MEC's "GL data ingestion timestamp" and this
// feature's own validation modal and list grid already do. FormattingService formats in the browser's local
// zone and labels nothing, so the same instant read as a different day either side of a timezone boundary.
// Both take epoch milliseconds, which is what every timestamp here is.

/**
 * How a pull's source is worded. `UI` is deliberately absent: a pull someone ran from this screen needs no
 * attribution, and "(by UI)" would be noise on the common case. A source with no wording of its own is
 * shown as sent rather than relabelled or dropped.
 */
const SOURCE_LABELS: Record<string, string> = {
    MEC: 'month-end close',
};
const UNATTRIBUTED_SOURCE = 'UI';

/**
 * When the pull finished, shown only once it has.
 *
 * `completedAt` is null until terminal, so there is nothing to show while a pull runs. On the rare terminal
 * status with no `completedAt`, nothing is shown rather than falling back to `startedAt`: labelling a start
 * time as a finish time would be worse than omitting it.
 */
function completionTime(pullStatus: PullStatusResponse): string {
    if (!isTerminal(pullStatus) || pullStatus.completedAt == null) {
        return '';
    }
    return DateUtils.formatTimestamp(pullStatus.completedAt);
}

/** " (by month-end close)", or nothing for a pull run from this screen. */
function attribution(source?: string): string {
    if (!source || source === UNATTRIBUTED_SOURCE) {
        return '';
    }
    return ` (by ${SOURCE_LABELS[source] ?? source})`;
}

/**
 * What the metadata row says when no execution is being tracked — no pull has been polled in this session
 * and none could be recovered for the period.
 *
 * Reports the most specific thing available and nothing beyond it:
 *
 * <ul>
 *   <li><b>the last pull's verdict</b> — from the period's own durable record: the outcome as a word, when
 *       it happened, and who ran it. This is the one claim that used to be impossible to make here.
 *   <li><b>a pull timestamp</b> — "GL Pull: Last pulled &lt;when&gt;". A row carries one, so a pull did write
 *       to this period, but nothing says how it went.
 *   <li><b>only a data timestamp</b> — "Data last updated &lt;when&gt;", under its own label, because an
 *       upload moves it too. Deliberately not shown under "GL Pull", which it would misattribute.
 *   <li><b>neither</b> — nothing. Absence of a timestamp is not evidence that nothing was pulled, and
 *       absence of a pull record is not evidence about the data, so no wording claims either.
 * </ul>
 */
function DurablePullState(props: { period?: SegmentPeriodInfo; lastPulledAt?: number; dataUpdatedAt?: number }) {
    const { period, lastPulledAt, dataUpdatedAt } = props;
    const presentation = pullPresentation(period?.lastPullStatus);
    if (presentation) {
        // Prefer the finish time. A pull still running has none, so its start time is shown, labelled as a
        // start rather than passed off as a completion.
        //
        // A pull that has FINISHED but recorded no finish time shows no time at all, matching
        // `completionTime` above: falling back to its start time would render "Failed started <when>",
        // which labels a finished pull as running and a start time as a finish. Every terminal path writes
        // a finish time, so this is a record written outside that path rather than a state to expect.
        const terminal = period?.lastPullTerminal === true;
        const running = !terminal && period?.lastPullCompletedAt == null;
        const when = period?.lastPullCompletedAt ?? (terminal ? undefined : period?.lastPullStartedAt);
        return (
            <span>
                <strong>GL Pull:</strong>{' '}
                <StatusIndicator type={presentation.type}>{presentation.label}</StatusIndicator>
                {when != null && (
                    <>
                        {' '}{running ? 'started ' : ''}
                        {/* The element holds the timestamp and nothing else, matching every other place
                            this class is used. */}
                        <span className="segmentPullStatusTime">{DateUtils.formatTimestamp(when)}</span>
                    </>
                )}
                {attribution(period?.lastPullSource)}
            </span>
        );
    }
    if (lastPulledAt != null) {
        return (
            <span>
                <strong>GL Pull:</strong>{' '}Last pulled{' '}
                <span className="segmentPullStatusTime">{DateUtils.formatTimestamp(lastPulledAt)}</span>
            </span>
        );
    }
    if (dataUpdatedAt != null) {
        return (
            <span>
                <strong>Data last updated:</strong>{' '}
                <span className="segmentPullStatusTime">{DateUtils.formatTimestamp(dataUpdatedAt)}</span>
            </span>
        );
    }
    return null;
}

/** One column's row in the breakdown: its name, where its pull stands, the rate applied, and any reason. */
function ColumnRow(props: { row: ColumnPullRow; showFxRate: boolean }) {
    const { row, showFxRate } = props;
    return (
        <tr>
            <td className="pullStatusColumnName">{row.label}</td>
            <td>
                {row.presentation
                    ? (
                        <StatusIndicator type={row.presentation.type}>
                            {row.presentation.label}
                        </StatusIndicator>
                    )
                    // A column the pull recorded a reason or a rate for but no status. Left blank rather
                    // than filled with a guess: the reason beside it is what is actually known.
                    : ''}
            </td>
            {showFxRate && <td className="pullStatusFxRate">{row.fxRate ?? ''}</td>}
            {/* The reason a column failed is the actionable part, so it is shown inline rather than
                left in a log. */}
            <td className="pullStatusError">{row.error ?? ''}</td>
        </tr>
    );
}

/**
 * The GL pull's state for the selected period, in the detail view's metadata row.
 *
 * <p>Two sources. The <b>tracked execution</b> (`pullStatus`) is the live one: the stage or outcome as a
 * word, and on hover the per-column breakdown plus, at the top, whatever the server said went wrong. It is
 * published by the toolbar — from its poll while a pull runs, and from the period's own last-pull id after
 * a reload — so it covers both the live and the recovered case. When there is none, the period's
 * <b>durable last-pull record</b> answers instead, which after a reload is what says whether the last pull
 * succeeded.
 *
 * <p><b>The status shown is the execution's own, never a category.</b> It distinguishes the stages
 * (`Pulling GL balances`, `Deriving values`) and the outcomes that must not be conflated — a partial failure
 * is a warning rather than an error, and a table with no GL columns reads as "Nothing to pull" rather than
 * as a successful pull. A value this screen has not been taught about is rendered verbatim rather than
 * recategorised.
 *
 * <p><b>Nothing here is inferred.</b> Every word comes from a field: the label from a status, the reason
 * from the server's own text, the timestamps from the record. Where a field is absent the row says less
 * rather than guessing — and it never says "not pulled", because no absence supports that claim.
 */
export default function SegmentPullStatusIndicator() {
    const context = useContext(SegmentTablesContext);
    const detail = context?.state?.detail;
    const pullStatus = detail?.pullStatus;
    const columns: ColumnDef[] = detail?.definition?.columns ?? [];
    const lastPulledAt = detail?.aggregates?.lastUpdated;
    // The selected period's own entry: it carries the durable last-pull record and the period's data
    // timestamp, both of which outlive the tracked execution.
    const period = (detail?.periods ?? []).find((p) => p.period === detail?.selectedPeriod);

    const presentation = pullPresentation(pullStatus?.overallStatus);
    if (!presentation) {
        // No pull tracked, or one whose status the server did not state at all. Fall back to what the
        // period and its rows durably record.
        // The definition's own per-period stamp. GetSegmentPeriods reports no timestamp - it derives the
        // period list from the LEGEND rows - so the definition, read for the selected period, is the source.
        return (
            <DurablePullState
                period={period}
                lastPulledAt={lastPulledAt}
                dataUpdatedAt={detail?.definition?.dataLastUpdated}
            />
        );
    }

    // A presentation exists only because a status does, so the tracked pull is present from here on.
    const tracked = pullStatus as PullStatusResponse;
    const rows = columnPullRows(tracked, columns);
    // The run's own failure only. A column's reason sits in its own row below, so repeating it here would
    // say the same thing twice on the same surface.
    const failureText = runFailureText(tracked);
    const rowsUpserted = tracked.rowsUpserted;
    // The FX-rate column appears only once a rate is actually reported. Nothing in the fleet writes
    // `fx_rates` today, so a column headed by it would be permanently blank — three empty cells implying the
    // pull applied no conversion, which is a claim, not an omission. Rendering it on demand means it starts
    // showing rates the moment a writer lands, with no further change here.
    const showFxRate = rows.some((row) => !!row.fxRate);

    const indicator = <StatusIndicator type={presentation.type}>{presentation.label}</StatusIndicator>;
    const finishedAt = completionTime(tracked);
    const timestamp = finishedAt
        ? <span className="segmentPullStatusTime">{finishedAt}</span>
        : null;

    // Nothing to drill into: no column has a status, a reason or a rate yet, and the run recorded no
    // failure of its own. The word stands alone rather than offering an empty popover.
    if (!rows.length && !failureText) {
        return <span><strong>GL Pull:</strong> {indicator} {timestamp}</span>;
    }

    return (
        <span>
            <strong>GL Pull:</strong>{' '}
            <Popover
                header="Column pull status"
                position="bottom"
                size="large"
                triggerType="custom"
                dismissButton={false}
                content={(
                    <>
                        {/* First, because it is the reason the rest of this is being read. A run-level
                            failure has no column to sit against, so before this it appeared nowhere at
                            all. Verbatim: it is the server's own text. */}
                        {failureText && (
                            <div className="segmentPullFailureReason">
                                <StatusIndicator type="error">{failureText}</StatusIndicator>
                            </div>
                        )}
                        {!!rows.length && (
                            <table className="segmentPullStatusTable">
                                {/* Headed only for the rate, which is the one column whose values do not
                                    say what they are: a bare "1.0834" beside a status and a reason is
                                    unreadable without a label, whereas the name, the status and the message
                                    are self-describing. The popover's own header names the rest. */}
                                {showFxRate && (
                                    <thead>
                                        <tr>
                                            <th aria-label="Column" />
                                            <th aria-label="Status" />
                                            <th className="pullStatusFxRateHeader">FX rate</th>
                                            <th aria-label="Reason" />
                                        </tr>
                                    </thead>
                                )}
                                <tbody>
                                    {rows.map((row) => (
                                        <ColumnRow key={row.columnId} row={row} showFxRate={showFxRate} />
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {/* Whether anything was actually written, which a partial failure leaves genuinely
                            open. Omitted when the server does not say. */}
                        {rowsUpserted != null && (
                            <div className="segmentPullRowsUpserted">{rowsUpserted} row(s) updated</div>
                        )}
                    </>
                )}
            >
                {/* Wrapped so the indicator itself is the hover target and reads as interactive. */}
                <span className="segmentPullStatusTrigger">{indicator}</span>
            </Popover>
            {/* Outside the popover trigger: the finish time is plain information, not something to hover. */}
            {timestamp && <>{' '}{timestamp}</>}
        </span>
    );
}
